// Read-only view of the seeded disposable / temp-mail blacklist. This list is
// tens of thousands of domains loaded by the seed (source='seed'), so instead
// of a giant table we show the total count, when it was last updated, a
// "Refresh list" button (re-downloads the maintained upstream list), and a
// search box that queries the server for matches.
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, TextField, InputAdornment, List, ListItem, ListItemText,
  CircularProgress, Alert, Chip, Button, Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  fetchSeedCount, searchSeedList, clearSeedSearch, refreshSeedList,
} from '../../../store/admin-blacklist-slice.js';

const formatRefreshedAt = (iso) => {
  if (!iso) return 'Never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const SeededDomainsSection = () => {
  const dispatch = useDispatch();
  const seed = useSelector((s) => s.adminBlacklist.seed);
  const [term, setTerm] = useState('');

  useEffect(() => {
    dispatch(fetchSeedCount());
  }, [dispatch]);

  // Debounce the search so each keystroke doesn't hit the server.
  useEffect(() => {
    const trimmed = term.trim();
    if (!trimmed) {
      dispatch(clearSeedSearch());
      return undefined;
    }
    const handle = setTimeout(() => dispatch(searchSeedList(trimmed)), 300);
    return () => clearTimeout(handle);
  }, [term, dispatch]);

  const countLabel = seed.count == null ? '…' : seed.count.toLocaleString();
  const trimmed = term.trim();

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1,
      }}
      >
        <Box>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            Disposable / temp-mail domains
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A maintained list of throwaway-email providers, blocked automatically.
            Use “Refresh list” to pull the latest version. To exempt a domain
            that was caught by mistake, add it to the allowlist below.
          </Typography>
        </Box>
        <Chip label={`${countLabel} domains`} color="default" />
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center', mt: 1, mb: 1, flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={seed.refreshing
            ? <CircularProgress size={16} color="inherit" />
            : <RefreshIcon fontSize="small" />}
          onClick={() => dispatch(refreshSeedList())}
          disabled={seed.refreshing}
        >
          {seed.refreshing ? 'Refreshing…' : 'Refresh list'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {`Last updated: ${formatRefreshedAt(seed.refreshedAt)}`}
        </Typography>
      </Stack>

      {seed.refreshError && <Alert severity="error" sx={{ mb: 1 }}>{seed.refreshError}</Alert>}

      <TextField
        fullWidth
        size="small"
        label="Search the disposable list"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        sx={{ mt: 1 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {seed.searchError && <Alert severity="error" sx={{ mt: 2 }}>{seed.searchError}</Alert>}

      {trimmed && (
        <Box sx={{ mt: 1 }} aria-live="polite">
          {seed.searching ? (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 1,
            }}
            >
              <CircularProgress size={18} aria-label="Searching" />
              <Typography variant="body2" color="text.secondary">Searching…</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                {seed.matches.length === 0
                  ? `No seeded domains match “${trimmed}”.`
                  : `Showing ${seed.matches.length} match${
                    seed.matches.length === 1 ? '' : 'es'} (max 50).`}
              </Typography>
              {seed.matches.length > 0 && (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                  {seed.matches.map((m) => (
                    <ListItem key={m.id} disableGutters sx={{ py: 0 }}>
                      <ListItemText
                        primaryTypographyProps={{ sx: { fontFamily: 'monospace' } }}
                        primary={m.pattern}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default SeededDomainsSection;
